'use client';

import { cn } from '@/lib/utils';
import type { KeyboardEvent, MouseEvent } from 'react';

interface SidebarSplitHandleProps {
  /**
   * Whether the boundary may be dragged. False on the mobile drawer, where
   * there is no pointer and this ~9px strip would sit under the 44px touch
   * floor (WCAG 2.1 AA SC 2.5.5) — there it renders as the plain, inert
   * divider it replaced. The 50/50 sizing still applies; only the handle goes.
   */
  canResize: boolean;
  /** Share of the flexible space currently given to the menu pane (0–1). */
  menuRatio: number;
  /** True mid-drag — pins the grabber visible and tints the rule. */
  isDragging: boolean;
  /** Accessible name, already localized by the caller. */
  label: string;
  onMouseDown: (event: MouseEvent) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  onReset: () => void;
}

/**
 * The boundary between the sidebar's nav block and its Recents list.
 *
 * It REPLACES the plain divider that used to sit here rather than living next
 * to it: the line the user sees and the control that moves it should be the
 * same thing. Implements the ARIA window-splitter pattern, so it is operable
 * by keyboard (arrows nudge, Home/End hit the floors, Enter/Space restore the
 * even split) as well as by drag — drag alone would put the feature out of
 * reach of keyboard users (WCAG 2.1.1).
 *
 * Extracted from SidebarRail so the rail's own render stays under Biome's
 * cognitive-complexity ceiling.
 */
export const SidebarSplitHandle = ({
  canResize,
  menuRatio,
  isDragging,
  label,
  onMouseDown,
  onKeyDown,
  onReset,
}: SidebarSplitHandleProps) => {
  if (!canResize) {
    return (
      <div
        aria-hidden='true'
        className='mx-2 my-0.5 shrink-0 border-t border-line-subtle'
      />
    );
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: <hr> is the semantic element for role="separator", but it is a decorative, non-interactive break with no value semantics — it cannot express a focusable, adjustable splitter (aria-valuenow + key handling).
    <div
      role='separator'
      aria-orientation='horizontal'
      aria-label={label}
      aria-valuenow={Math.round(menuRatio * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
      onDoubleClick={onReset}
      className={cn(
        'group/split relative mx-2 my-0.5 shrink-0 cursor-row-resize border-t py-1 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tertiary',
        isDragging
          ? 'border-brand-tertiary'
          : 'border-line-subtle hover:border-brand-tertiary/60'
      )}
    >
      <span
        aria-hidden='true'
        className={cn(
          'absolute left-1/2 top-1/2 h-1 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-line-strong transition-opacity',
          isDragging ? 'opacity-100' : 'opacity-0 group-hover/split:opacity-100'
        )}
      />
    </div>
  );
};
